import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useScope } from "~/hooks/useScope";
import { useGetWorkflowInitialQuery, WorkflowStatus } from "~/operations";
import buildWorkflowMrn from "../utils";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";

export default function useWorkflowDetail() {
  const { spaceScope } = useScope();
  const params = useParams();
  const workflowId = params.workflowId;
  const workflowMrn = buildWorkflowMrn(spaceScope?.id, workflowId);
  const { integration } = useSecurityPipelineIntegrations();
  const integrationId = integration?.mrn.split("/").pop();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingInterval = 5 * 1000; // 5 seconds
  const POLLING_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  const { data, error, loading, startPolling, stopPolling } =
    useGetWorkflowInitialQuery({
      variables: {
        mrn: workflowMrn,
      },
      fetchPolicy: "cache-and-network",
      skip: !workflowMrn,
    });

  const shouldVoidPolling = useMemo(() => {
    if (
      data?.securityPipelineWorkflow?.__typename === "SecurityPipelineWorkflow"
    ) {
      // we should stop polling if the workflow is in a terminal state
      const invalidStatuses = [
        WorkflowStatus.Canceled,
        WorkflowStatus.Failed,
        WorkflowStatus.Fixed,
      ];
      return invalidStatuses.includes(data.securityPipelineWorkflow.status);
    }
    return false;
  }, [data]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (shouldVoidPolling || error) {
      stopPolling();
    } else {
      startPolling(pollingInterval);

      timeoutRef.current = setTimeout(() => {
        stopPolling();
        timeoutRef.current = null;
      }, POLLING_TIMEOUT);
    }

    return () => {
      stopPolling();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [error, workflowMrn, shouldVoidPolling, startPolling, stopPolling]);

  const workflow =
    data?.securityPipelineWorkflow?.__typename === "SecurityPipelineWorkflow"
      ? data.securityPipelineWorkflow
      : {
          mrn: "",
          ownerMrn: "",
          integrationMrn: "",
          title: "",
          status: WorkflowStatus.Pending,
          pullRequest: { title: "", url: "" },
          createdBy: null,
          createdAt: "",
          updatedAt: "",
          tasks: [],
          labels: [],
          findingGroups: [],
          scheduledWorkflowMrn: null,
        };

  return {
    error,
    loading,
    workflowMrn,
    headerData: {
      status: workflow.status,
      title: workflow.title,
      createdBy: workflow.createdBy,
      createdAt: workflow.createdAt,
      pullRequest: workflow.pullRequest,
      workflowMrn: workflow.mrn,
      integrationId,
    },
    progressData: {
      tasks: workflow.tasks,
    },
    scopeData: {
      space: spaceScope,
      listItems: workflow.findingGroups,
    },
  };
}
